import React, { useState, useEffect, useCallback } from 'react';
import { activityLogsAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  UserPlus, Edit, Trash2, FileUp, FileX, Receipt, ReceiptText,
  CheckCircle2, RefreshCw, Filter, ChevronLeft, ChevronRight, Clock
} from 'lucide-react';
import logger from '@/lib/logger';

const ACTION_META = {
  client_created:   { icon: UserPlus,     color: 'bg-emerald-500', label: 'Client Created' },
  client_updated:   { icon: Edit,         color: 'bg-blue-500',    label: 'Client Updated' },
  client_deleted:   { icon: Trash2,       color: 'bg-red-500',     label: 'Client Deleted' },
  document_uploaded:{ icon: FileUp,       color: 'bg-violet-500',  label: 'Document Uploaded' },
  document_deleted: { icon: FileX,        color: 'bg-rose-500',    label: 'Document Deleted' },
  invoice_created:  { icon: Receipt,      color: 'bg-teal-500',    label: 'Invoice Created' },
  invoice_updated:  { icon: ReceiptText,  color: 'bg-amber-500',   label: 'Invoice Updated' },
  invoice_deleted:  { icon: Trash2,       color: 'bg-red-500',     label: 'Invoice Deleted' },
  status_filed:     { icon: CheckCircle2, color: 'bg-green-600',   label: 'Status Filed' },
};

const ENTITY_TYPES = [
  { value: '', label: 'All Types' },
  { value: 'client', label: 'Clients' },
  { value: 'document', label: 'Documents' },
  { value: 'invoice', label: 'Invoices' },
  { value: 'task', label: 'Tasks' },
];

const PAGE_SIZE = 30;

const ActivityLogPage = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [entityFilter, setEntityFilter] = useState('');

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (entityFilter) params.entity_type = entityFilter;
      const res = await activityLogsAPI.list(params);
      setLogs(res.data.logs);
      setTotal(res.data.total);
    } catch (err) {
      logger.error('Failed to load activity logs', err);
    }
    setLoading(false);
  }, [page, entityFilter]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'Just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 7) return `${diffDay}d ago`;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatFullTime = (iso) => {
    if (!iso) return '';
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6" data-testid="activity-log-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-semibold">Activity Log</h2>
          <p className="text-sm text-muted-foreground mt-1">{total} total entries</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={entityFilter || "all"} onValueChange={(v) => { setEntityFilter(v === "all" ? "" : v); setPage(0); }}>
              <SelectTrigger className="w-40 h-9" data-testid="entity-type-filter-trigger">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                {ENTITY_TYPES.map(t => (
                  <SelectItem key={t.value || "all"} value={t.value || "all"}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button variant="outline" size="sm" onClick={fetchLogs} data-testid="refresh-logs">
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Timeline */}
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-muted rounded-lg animate-pulse" />)}
        </div>
      ) : logs.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center">
            <Clock className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-semibold mb-1">No activity yet</h3>
            <p className="text-sm text-muted-foreground">Actions like creating clients, uploading documents, or managing invoices will appear here.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <ScrollArea className="max-h-[calc(100vh-280px)]">
            <div className="divide-y">
              {logs.map((log) => {
                const meta = ACTION_META[log.action] || { icon: Clock, color: 'bg-gray-500', label: log.action };
                const Icon = meta.icon;
                return (
                  <div key={log.id} className="flex items-start gap-4 px-4 py-3.5 hover:bg-muted/30 transition-colors" data-testid="activity-log-entry">
                    <div className={`mt-0.5 flex-shrink-0 h-8 w-8 rounded-full ${meta.color} flex items-center justify-center`}>
                      <Icon className="h-4 w-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium">{meta.label}</span>
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0">{log.entity_type}</Badge>
                      </div>
                      <p className="text-sm text-foreground mt-0.5 truncate">
                        <span className="font-medium">{log.entity_name}</span>
                        {log.details && <span className="text-muted-foreground"> - {log.details}</span>}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs text-muted-foreground">by {log.user_name || 'System'}</span>
                        <Badge variant="secondary" className="text-[10px] px-1 py-0">{log.user_role}</Badge>
                        <span className="text-xs text-muted-foreground" title={formatFullTime(log.created_at)}>{formatTime(log.created_at)}</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <span className="text-xs text-muted-foreground">
                Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="prev-page">
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm">{page + 1} / {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="next-page">
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default ActivityLogPage;
